import type { TwilioConfigInput as NullableTwilioConfigInput } from '@pypestream/api-services';
import type { TwilioConfigInput } from '@pypestream/api-services/urql';

// Imported from the module, not the `utils` barrel: the barrel drags in the
// XState graph and with it `api-services`, which this package's vitest setup
// stubs — a barrel import makes this builder untestable in isolation.
import { encodeToBase64 } from '../../utils/encode-base64';
// The leaf module, not the `utils` barrel and not `contact-center-voice`: both
// reach `api-services/urql`, whose subpath this package's vitest resolves to the
// REAL file, which would force every test of this builder to mock it.
import {
  VOICE_INBOX_HANDOFF_TYPE,
  type ContactCenterIntegrationInfo,
} from '../../utils/voice-handoff-types';
import { resolveConversationHandling } from './flux-rules';
import {
  encodeLanguagesConfig,
  LANGUAGE_TTS_PROVIDER,
} from './languages-config';
import type { VoiceChannelFormValues } from './voice-channel-settings-form';

type FlagAccess = 'allowed' | 'denied' | 'pending';

/** Saving connects the inbox and writes escalation rows: master flag only. */
export const setsUpVoiceInbox = (masterFlag: FlagAccess): boolean =>
  masterFlag === 'allowed';

/** Everything the payload needs; `botName` and `env` are channel-level, not config. */
export type VoiceAiConfigFormData = Omit<
  VoiceChannelFormValues,
  'botName' | 'env'
>;

/**
 * Builds a Voice AI channel's complete `twilio_params` payload.
 *
 * Extracted from the form (the way `resolveConversationHandling` already was)
 * so the one rule that is easy to break silently can be tested: this payload
 * REBUILDS THE WHOLE MAP, and the Hasura request transform nulls every key the
 * client omits. A field dropped here is not a missing field — it is that
 * setting being cleared on the next save of anything else (PSTUDIO-4090).
 *
 * Since PSTUDIO-2950 the voice comes from the Languages block. The flat
 * `language` / `elevenlabs*` keys describe the STARTING language: they are what
 * a call runs on whenever the overlay is absent or rejected, so they must stay
 * correct even when `languagesConfigB64` carries every language.
 *
 * The return type makes `enableCallRecording` required, which the generated
 * `TwilioConfigInput` does not, so the omission at least reads as intentional.
 * The typecheck catches a mistyped field, but not a dropped one, because every
 * key is optional. The tests are the gate for that.
 *
 * The `null`s are deliberate: JSON `null` clears a stored value, and an
 * omitted key would do the same through the request transform, only silently.
 * The urql input type cannot express them, because `graphql.urql.ts` is
 * generated with `maybeValue: 'T'` (api-services `codegen.ts`) although the
 * schema declares these inputs nullable. So the payload is built against the
 * nullable variant, and converted once, here, for the urql-typed chain
 * (`ChannelPayload.voiceAiConfig` → `mutateChannel`).
 */
export const buildVoiceAiConfig = (
  formData: VoiceAiConfigFormData,
  contactCenter?: ContactCenterIntegrationInfo | null
): TwilioConfigInput & { enableCallRecording: boolean } => {
  const config: NullableTwilioConfigInput & { enableCallRecording: boolean } =
    buildNullableVoiceAiConfig(formData, contactCenter);
  return config as TwilioConfigInput & { enableCallRecording: boolean };
};

const buildNullableVoiceAiConfig = (
  formData: VoiceAiConfigFormData,
  contactCenter?: ContactCenterIntegrationInfo | null
): NullableTwilioConfigInput & { enableCallRecording: boolean } => {
  // Validation keeps the starting key among the entries; the fallback only
  // guarantees the payload never goes out without a flat language at all.
  const starting =
    formData.languages.find((l) => l.key === formData.startingLanguage) ??
    formData.languages[0];

  const encodedMetadata = starting?.elevenlabsVoiceMetadata
    ? encodeToBase64(JSON.stringify(starting.elevenlabsVoiceMetadata))
    : null;

  const hasLaConfig =
    formData.laPlatform ||
    formData.laClientId ||
    formData.laClientSecret ||
    formData.laGrantType ||
    formData.laInstanceUrl ||
    formData.laOAuthUrl;

  /**
   * One slot, two shapes. `metadata_integration_info_b64` is read by whoever
   * recognises the keys it finds, so the Contact Center blob REPLACES the
   * Genesys credential blob rather than merging with it — a merged config would
   * still be routed by `integrator` alone, and leaving a stale `client_secret`
   * beside the Contact Center ids persists a credential nothing will ever use.
   *
   * Contact Center wins when a connection is selected: `contactCenter` is
   * resolved from that selection, and the Genesys fields are not rendered while
   * it is in force, so anything still sitting in them is leftover form state.
   */
  const encodedLaConfig = contactCenter
    ? encodeToBase64(JSON.stringify(contactCenter))
    : hasLaConfig
      ? encodeToBase64(
          JSON.stringify({
            integrator: formData.laPlatform,
            client_id: formData.laClientId,
            client_secret: formData.laClientSecret,
            grant_type: formData.laGrantType,
            instance_url: formData.laInstanceUrl,
            o_auth_url: formData.laOAuthUrl,
          })
        )
      : null;

  // A `voice_inbox` rule carries NO destination — the destination is the
  // Contact Center inbox, which Engagement resolves from the channel's
  // integration info. Requiring one here (as the filter did for every rule)
  // dropped the rule on the floor and left the skill unrouted.
  const handoffRules = formData.handoffRules?.filter(
    (r) => r.skillTag && (r.destination || r.type === VOICE_INBOX_HANDOFF_TYPE)
  );
  const encodedHandoffOptions =
    handoffRules?.length > 0
      ? encodeToBase64(
          JSON.stringify(
            Object.fromEntries(
              handoffRules.map(
                ({ skillTag, destination, type: handoffType }) => [
                  skillTag,
                  // The destination key is omitted, not sent empty: Engagement
                  // validates `type` + `destination` together and only falls
                  // through to the Voice Inbox branch when that pair is not a
                  // valid phone/sip destination. An empty string takes the same
                  // branch today, but saying "there is no destination" is the
                  // honest payload and does not depend on that coincidence.
                  // The Type select SHOWS "Phone number" when the rule's type
                  // is unset, but never writes it to the form — so a rule saved
                  // without touching it went out with no `type` at all.
                  handoffType === VOICE_INBOX_HANDOFF_TYPE
                    ? { type: handoffType }
                    : { destination, type: handoffType ?? 'phone_number' },
                ]
              )
            )
          )
        )
      : null;

  const conversationHandling = resolveConversationHandling({
    speechModel: formData.speechModel,
    inboundMessageBuffer: formData.inboundMessageBuffer,
    userSilenceTimeout: formData.userSilenceTimeout,
    eotThreshold: formData.eotThreshold,
  });

  return {
    accountSid: formData.accountSid,
    authToken: formData.authToken,
    language: starting?.locale,
    phoneNumber: formData.phoneNumber,
    speechModel: formData.speechModel,
    transcriptionProvider: formData.transcriptionProvider,
    ttsProvider: LANGUAGE_TTS_PROVIDER,
    elevenlabsAudioModel: starting?.elevenlabsAudioModel,
    elevenlabsVoiceId: starting?.elevenlabsVoiceId,
    elevenlabsSimilarity: String(starting?.elevenlabsSimilarity),
    elevenlabsSpeed: String(starting?.elevenlabsSpeed),
    elevenlabsStability: String(starting?.elevenlabsStability),
    elevenlabsVoiceMetadataB64: encodedMetadata,
    // Always sent, null included: like every key here it is cleared when
    // omitted, and an explicit null is what drops a stale overlay.
    languagesConfigB64: encodeLanguagesConfig(
      formData.languages,
      formData.startingLanguage
    ),
    metadataIntegrationInfoB64: encodedLaConfig,
    voiceHandoffOptionsB64: encodedHandoffOptions,
    userSilenceTimeout: conversationHandling.userSilenceTimeout,
    inboundMessageBufferMs: conversationHandling.inboundMessageBufferMs,
    // TODO(PSTUDIO-3630): send conversationHandling.eotThreshold once the
    // backend exposes TwilioConfigInput.eotThreshold. GraphQL rejects an entire
    // mutation containing an unknown input field, so sending it early would
    // break every voice channel save, not just this setting.
    promptMaxRetries: formData.promptMaxRetries,
    retryPromptPrefix: formData.retryPromptPrefix,
    // Always sent, touched or not — see VoiceChannelFormValues. Coerced
    // because `initialValues` is Partial: a caller omitting the key would
    // otherwise drop it from the JSON and reintroduce the same bug.
    enableCallRecording: formData.enableCallRecording === true,
  };
};
